/**
 * AccountantResultCard: one row in the Find an Accountant results list.
 *
 * Classical card: dark-ink initials avatar on the left, name/firm/location
 * block, a thin inline list of top services, small meta row (industry,
 * products, years), and two actions on the right (View profile, Contact).
 *
 * No colored background pills: service and product affiliations are plain
 * text with soft underline/border. Verified checkmark uses the Novala
 * teal because it is a success/verified indicator.
 *
 * The card is purely presentational. All data arrives via props from the
 * useFindAccountants hook via the page composer.
 */

import { useId } from "react";
import { MapPin, Mail, CheckCircle2, Award } from "lucide-react";
import type { DirectoryAccountant } from "../findAccountantTypes";
import {
  INDUSTRY_LABELS,
  PRODUCT_LABELS,
  SERVICE_LABELS,
} from "../findAccountantMockData";
import { getInitials } from "../../myAccountantUtils";

interface AccountantResultCardProps {
  accountant: DirectoryAccountant;
  onViewProfile?: (id: string) => void;
  onContact?: (id: string) => void;
}

const MAX_INLINE_SERVICES = 4;

export function AccountantResultCard({
  accountant,
  onViewProfile,
  onContact,
}: AccountantResultCardProps) {
  const uid = useId().replace(/:/g, "");
  const styleTag = useResultCardStyles(uid);

  const initials = getInitials(accountant.name, accountant.email ?? accountant.name);
  const topServices = accountant.services.slice(0, MAX_INLINE_SERVICES);
  const extraServiceCount = Math.max(
    0,
    accountant.services.length - MAX_INLINE_SERVICES,
  );
  const industriesLine = accountant.industries
    .map((i) => INDUSTRY_LABELS[i])
    .join(", ");
  const productsLine = accountant.products
    .map((p) => PRODUCT_LABELS[p])
    .join(", ");

  return (
    <>
      {styleTag}
      <article className={`arc-${uid}-card`}>
        <div className={`arc-${uid}-avatar`} aria-hidden="true">
          {initials}
        </div>

        <div className={`arc-${uid}-main`}>
          <div className={`arc-${uid}-head`}>
            <h3 className={`arc-${uid}-name`}>
              {accountant.name}
              <CheckCircle2
                size={14}
                strokeWidth={2.4}
                className={`arc-${uid}-verified`}
                aria-label="Novala-verified"
              />
            </h3>
            <p className={`arc-${uid}-firm`}>{accountant.firmName}</p>
            <p className={`arc-${uid}-loc`}>
              <MapPin size={12} strokeWidth={2} />
              <span>
                {accountant.city}, {accountant.province}
                <span className={`arc-${uid}-dot`}>·</span>
                <span className={`arc-${uid}-dist`}>
                  {accountant.distanceKm} km away
                </span>
              </span>
            </p>
          </div>

          <ul className={`arc-${uid}-services`}>
            {topServices.map((slug) => (
              <li key={slug} className={`arc-${uid}-service`}>
                {SERVICE_LABELS[slug]}
              </li>
            ))}
            {extraServiceCount > 0 && (
              <li className={`arc-${uid}-service arc-${uid}-service-more`}>
                +{extraServiceCount} more
              </li>
            )}
          </ul>

          <dl className={`arc-${uid}-meta`}>
            <div className={`arc-${uid}-meta-row`}>
              <dt>Industries</dt>
              <dd>{industriesLine || "Any industry"}</dd>
            </div>
            <div className={`arc-${uid}-meta-row`}>
              <dt>Certified in</dt>
              <dd>{productsLine}</dd>
            </div>
            <div className={`arc-${uid}-meta-row`}>
              <dt>Experience</dt>
              <dd>
                <Award size={12} strokeWidth={2} className={`arc-${uid}-award`} />
                {accountant.yearsExperience} years in practice
              </dd>
            </div>
          </dl>
        </div>

        <div className={`arc-${uid}-actions`}>
          <button
            type="button"
            className={`arc-${uid}-btn-primary`}
            onClick={() => onViewProfile?.(accountant.id)}
          >
            View profile
          </button>
          <button
            type="button"
            className={`arc-${uid}-btn-secondary`}
            onClick={() => onContact?.(accountant.id)}
          >
            <Mail size={13} strokeWidth={2} />
            <span>Contact</span>
          </button>
        </div>
      </article>
    </>
  );
}

// =====================================================================
// Styles
// =====================================================================

function useResultCardStyles(uid: string) {
  const css = `
    .arc-${uid}-card {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 20px;
      padding: 22px 24px;
      background: #ffffff;
      border: 1px solid #E7EAF0;
      border-radius: 10px;
      margin-bottom: 12px;
      transition: border-color 180ms, box-shadow 180ms, transform 180ms;
    }
    .arc-${uid}-card:hover {
      border-color: #D8DEE6;
      box-shadow: 0 8px 18px -6px rgba(14, 26, 26, 0.10);
      transform: translateY(-1px);
    }

    .arc-${uid}-avatar {
      width: 54px;
      height: 54px;
      border-radius: 50%;
      background: #0E1A1A;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 17px;
      letter-spacing: 0.02em;
      flex-shrink: 0;
    }

    .arc-${uid}-main {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .arc-${uid}-head { display: flex; flex-direction: column; gap: 3px; }
    .arc-${uid}-name {
      font-size: 15.5px;
      font-weight: 600;
      color: #0E1A1A;
      margin: 0;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      line-height: 1.3;
    }
    .arc-${uid}-verified { color: #0F6E56; flex-shrink: 0; }
    .arc-${uid}-firm {
      font-size: 13px;
      color: #3A4A52;
      margin: 0;
      font-weight: 500;
    }
    .arc-${uid}-loc {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12.5px;
      color: #6B7684;
      margin: 2px 0 0;
    }
    .arc-${uid}-loc svg { color: #6B7684; flex-shrink: 0; }
    .arc-${uid}-dot { margin: 0 6px; color: #B0B9C3; }
    .arc-${uid}-dist { color: #3A4A52; font-weight: 500; }

    .arc-${uid}-services {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 6px 10px;
    }
    .arc-${uid}-service {
      font-size: 12px;
      color: #0E1A1A;
      padding: 3px 10px;
      border: 1px solid #E7EAF0;
      border-radius: 999px;
      background: #FAFBFC;
      font-weight: 500;
    }
    .arc-${uid}-service-more {
      color: #3A4A52;
      font-weight: 400;
      background: transparent;
      border-color: transparent;
      padding-left: 4px;
    }

    .arc-${uid}-meta {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin: 0;
      padding: 10px 0 0;
      border-top: 1px solid #F3F5F5;
    }
    .arc-${uid}-meta-row {
      display: grid;
      grid-template-columns: 110px 1fr;
      gap: 10px;
      font-size: 12.5px;
      line-height: 1.5;
    }
    .arc-${uid}-meta-row dt {
      color: #6B7684;
      font-weight: 500;
    }
    .arc-${uid}-meta-row dd {
      margin: 0;
      color: #0E1A1A;
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
    .arc-${uid}-award { color: #3A4A52; flex-shrink: 0; }

    .arc-${uid}-actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: stretch;
      justify-content: flex-start;
      min-width: 130px;
    }
    .arc-${uid}-btn-primary {
      background: #0E1A1A;
      color: #ffffff;
      border: 0;
      padding: 9px 14px;
      border-radius: 7px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: transform 160ms, box-shadow 160ms;
    }
    .arc-${uid}-btn-primary:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 10px -2px rgba(14, 26, 26, 0.22);
    }
    .arc-${uid}-btn-secondary {
      background: #ffffff;
      color: #0E1A1A;
      border: 1px solid #D8DEE6;
      padding: 8px 12px;
      border-radius: 7px;
      font-size: 12.5px;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: border-color 160ms, background 160ms;
    }
    .arc-${uid}-btn-secondary:hover {
      border-color: #0E1A1A;
      background: #FAFBFC;
    }

    @media (max-width: 760px) {
      .arc-${uid}-card {
        grid-template-columns: auto 1fr;
      }
      .arc-${uid}-actions {
        grid-column: 1 / -1;
        flex-direction: row;
        min-width: 0;
      }
      .arc-${uid}-btn-primary, .arc-${uid}-btn-secondary { flex: 1; }
      .arc-${uid}-meta-row {
        grid-template-columns: 1fr;
        gap: 0;
      }
      .arc-${uid}-meta-row dt { font-size: 11px; }
    }
  `;
  return <style>{css}</style>;
}